import {Button} from "@zendeskgarden/react-buttons";
import {Field, Input, Label} from '@zendeskgarden/react-forms';
import {ReactElement, useContext, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {validateOpDriverName} from "../../../../../../components/account-table/logic/DriverHelpers";
import {getNewOpDriverName} from "../../../../../../contexts/account/data/logic/driversAndWorksheetData";
import {IFormulaBarUpdates} from "../../../../../../contexts/account/data/useFormulaBarUpdates";
import {AuthContext} from "../../../../../../contexts/AuthContext";
import css from "./styles/operationalDriverMenu.module.scss";

const MANUAL_ENTRY_METRIC_TYPE = "Manual Entry";
export interface IOperationalDriverMenu {
    fbUpdates: IFormulaBarUpdates;
    handleCloseParent: () => void;
}

export default function OperationalDriverMenu({
    fbUpdates,
    handleCloseParent,
}: IOperationalDriverMenu): ReactElement {
    const authContext = useContext(AuthContext);
    const opDriverMenuOptions = authContext.operationalMetricTypes.map(x => x.name);

    const [driverName, setDriverName] = useState<string>("");
    const [chosenDriverType, setChosenDriverType] = useState<string | null>(null);
    const [chosenSecondaryDriverType, setChosenSecondaryDriverType] = useState<string | null>(null);

    const newDriverNameInput = useRef<HTMLInputElement>(null);

    function saveDriver() {
        if (!chosenDriverType || !chosenSecondaryDriverType) {
            return;
        }

        const existingDriverNames = fbUpdates.parsedFormDrivers.operational.map(x => ({name: x.name, type: x.type}));
        const newDriverNameType = {name: driverName, type: chosenDriverType};
        const validityCheck = validateOpDriverName(newDriverNameType, existingDriverNames);

        if (!validityCheck.isValid) {
            toast.error(validityCheck.error);
            return;
        }

        const sourceMetricId = authContext.operationalMetricTypes.find(x => x.name === chosenDriverType)?.id;
        let secondaryMetricId = undefined;
        if (chosenSecondaryDriverType !== MANUAL_ENTRY_METRIC_TYPE) {
            secondaryMetricId = authContext.operationalMetricTypes.find(x => x.name === chosenSecondaryDriverType)?.id;
        }

        if (chosenDriverType && sourceMetricId && chosenSecondaryDriverType) {
            const createPayload = {
                type: chosenDriverType,
                secondaryType: chosenSecondaryDriverType,
                amount: undefined,
                sourceMetricId,
                secondaryMetricId,
                driverMetricTypeId: '',
                lookbackPeriod: 0,
                percentage: new Array(12).fill(0),
                values: new Array(12).fill(0),
                locked: false,
                valuesLocked: false,
                configLocked: false,
                name: driverName,
                cumSum: false,
                nonProrated: false,
                minValue: null,
                maxValue: null,
            };
            fbUpdates.addOperationalDriver({createPayload});
            handleCloseParent();
        }
    }

    useEffect(() => {
        if (chosenDriverType && newDriverNameInput.current) {
            const existingDriverNameTypes = fbUpdates.parsedFormDrivers.operational.map(x => ({name: x.name, type: x.type}));
            const newDefaultDriverName = getNewOpDriverName(existingDriverNameTypes, chosenDriverType, chosenSecondaryDriverType && chosenSecondaryDriverType !== MANUAL_ENTRY_METRIC_TYPE ? chosenSecondaryDriverType : null);
            setDriverName(newDefaultDriverName);

            newDriverNameInput.current.focus();
        } else {
            setDriverName("");
        }
    }, [chosenDriverType, chosenSecondaryDriverType, newDriverNameInput]);

    return (
        <>
            {
                chosenDriverType && chosenSecondaryDriverType ?
                <div className={css.inputWrapper}>
                    <Field>
                        <Label>Driver Name ({chosenDriverType}){chosenSecondaryDriverType && chosenSecondaryDriverType !== MANUAL_ENTRY_METRIC_TYPE ? ` + ${chosenSecondaryDriverType}` : ""}</Label>
                        <Input
                            ref={newDriverNameInput}
                            defaultValue={driverName}
                            onChange={evt => setDriverName(evt.target.value)}
                            onKeyDown={(evt) => {
                                if (evt.key === "Enter") {
                                    saveDriver();
                                }
                            }}
                        />
                    </Field>
                    <div className={css.actions}>
                        <Button size="small" isBasic isDanger onClick={() => {setChosenDriverType(null); setChosenSecondaryDriverType(null);}}>Back</Button>
                        <Button size="small" onClick={saveDriver}>Save</Button>
                    </div>
                </div>
                :
                chosenDriverType ?
                <div className={css.menuWrapper}>
                    <h4 className={css.header}>Secondary Operational Driver ({chosenDriverType})</h4>
                    <ul className={css.optionsWrapper}>
                        <>
                            <li><Button isBasic onClick={() => {setChosenSecondaryDriverType(MANUAL_ENTRY_METRIC_TYPE);}}>Manual Entry</Button></li>
                            {
                                opDriverMenuOptions.filter(driverType => driverType !== chosenDriverType).map(driverType =>
                                    <li>
                                        <Button isBasic onClick={() => {setChosenSecondaryDriverType(driverType);}}>{driverType}</Button>
                                    </li>
                                )
                            }
                        </>
                    </ul>
                </div>
                :
                <div className={css.menuWrapper}>
                    <h4 className={css.header}>Operational Drivers</h4>
                    <ul className={css.optionsWrapper}>
                        {
                            opDriverMenuOptions.map(driverType =>
                                <li>
                                    <Button isBasic onClick={() => setChosenDriverType(driverType)}>{driverType}</Button>
                                </li>
                            )
                        }
                    </ul>
                </div>
                }
        </>
    );
}
